import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import MaterialSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// component
import Notification from "./NotificationComponent";
import { clearDismissedBanners } from "Store/Actions/systemBannerActionCreator";

// images
import images from "Assets/images";

// css
import "./style.css";
import "Assets/css/topbar.css";
import "Assets/css/customize.css";
// Helper
import {
  getLocalStorage,
  overSession,
  setLocalStorage,
  get,
} from "Network/ApiService";
import { ls_vars, routes } from "Library/Constants";
import useOutsideClick from "./ManageOutsideClickClose";
import notification from "StaticData/Dashboard/notification";
import { headerMenu } from "StaticData/Dashboard/profile";
import { setBookingListFilter } from "Store/Actions/BookingList/BookingListActionCreator";
import { useNav } from "Hooks/useNav";
import { getSiteInfoAction } from "Store/Slices/projectReducer";
import { User } from "Types/User";
import AccountMenu from "Components/UI/AccountMenu";
import icons from "Assets/images/icons";
import { CHOOSE_PLATFORM_PAGE, ApiUrl } from "Network/ApiUrl";

// FCM integration for push notifications
import { initializePushNotifications, selectPushNotificationState } from "Store/Slices/pushNotificationSlice";
import toast from "react-hot-toast";

const index = () => {
  const { t } = useTranslation();
  const { viewHome } = useNav();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user: User = getLocalStorage(ls_vars.USER_DATA);
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  const projectsList = getLocalStorage(ls_vars.USER_PROJECTS) || [];

  const initials = user
    ? user?.firstName?.charAt(0) + user?.lastName?.charAt(0)
    : "NA";

  const refOutsideNotification = useRef(null);

  const [selectedProject, setSelectedProject] = useState<any>(projectRef);
  const [showNotification, setNotification] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  // Get push notification state from Redux
  const pushNotificationState = useSelector(selectPushNotificationState);
  const {
    subscriptionStatus = 'disabled',
    error: fcmError = null,
    isSupported = false
  } = pushNotificationState || {};

  // Initialize push notifications safely (only once)
  useEffect(() => {
    if (isSupported === false && fcmError === null) {
      // Only initialize if we haven't checked support yet
      dispatch(initializePushNotifications());
    }
  }, [dispatch, isSupported, fcmError]);

  // State for unread notifications count
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await get(ApiUrl.notifications.unreadCount);
        if (response?.data?.count !== undefined) {
          setUnreadCount(response.data.count);
        }
      } catch (error) {
        toast.error(t("Translation.err_getting_unread_count"));
      }
    };

    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = async (e: any) => {
    const selectedRef = e.target.value;
    const project = projectsList.find((el: any) => el.ref === selectedRef);
    if (project) {
      setSelectedProject(project);
      setLocalStorage(ls_vars.PROJECT_REF, project.ref);
      setLocalStorage(ls_vars.PROJECT_DATA, JSON.stringify(project));
      setLocalStorage(ls_vars.PROJECT_TIMEZONE, project.timezone);
      const projectRole = user.projects?.find(
        (el) => el.ref === selectedRef
      )?.role;
      if (projectRole)
        setLocalStorage(ls_vars.PROJECT_ROLE, JSON.stringify(projectRole));
      
      // Clear dismissed banners when project changes
      dispatch(clearDismissedBanners());
      
      dispatch(getSiteInfoAction(project.ref));
      dispatch(setBookingListFilter({}));
      viewHome(project.ref);
    }
  };

  const handleClickNotification = () => {
    navigate(routes.notifications.center);
  };

  useOutsideClick(refOutsideNotification, () => {
    if (showNotification) {
      setNotification(false);
    }
  });

  const logout = () => {
    overSession();
  };

  const isIllegalProject = !projectsList
    ?.map((el: any) => el.ref)
    .includes(projectRef);

  return (
    <>
      <div className="top-header" id="navbar-desktop">
        <div className="header-logo">
          <Link
            className="sidebar-brand d-flex mb-0"
            to={routes.dashboardNav.home(projectRef)}
          >
            <img src={images.logoMini} alt="logo" className="logo-mini" />
            <img src={images.logoGreenSvg} alt="logo" className="logo" />
          </Link>
        </div>
        <div className="header-navbar">
          <nav className="navbar navbar-expand navbar-light topbar static-top shadow pl-0">
            <form>
              <div className="form-group mb-0">
                <MaterialSelect
                  style={{ width: "350px", height: "45px" }}
                  value={projectRef}
                  defaultValue={selectedProject?.ref}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 233,
                        width: 250,
                      },
                    },
                  }}
                  onChange={handleChange}
                  renderValue={(selectedRef: string) => {
                    const project = projectsList.find(
                      (el: any) => el.ref === selectedRef
                    ) || {
                      logoImage: images.project1,
                      name: "Outside Project",
                    };
                    return (
                      <>
                        <div className="custm-header-select">
                          <div className="select-img-icon">
                            <img
                              className="profile-icon"
                              width="32px"
                              src={
                                project?.logoImage !== null
                                  ? project?.logoImage
                                  : images.project1
                              }
                            ></img>
                            <div className="select-project-text">
                              {project?.name}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  }}
                >
                  {isIllegalProject && (
                    <MenuItem value={projectRef} key={projectRef} disabled>
                      <div className="custm-header-select">
                        <div className="select-img-icon">
                          <img
                            className="profile-icon"
                            width="32px"
                            src={images.project1}
                          ></img>
                          <div className="select-project-text">
                            Outside Project
                          </div>
                        </div>
                        <img
                          className="select-check-icon"
                          src={images.CheckGreenIcon}
                          alt=""
                        />
                      </div>
                    </MenuItem>
                  )}
                  {projectsList?.map((item: any, i: number) => (
                    <MenuItem value={item.ref} key={item.label + i}>
                      <div className="custm-header-select">
                        <div className="select-img-icon">
                          <img
                            className="profile-icon"
                            width="32px"
                            src={
                              item?.logoImage !== null
                                ? item?.logoImage
                                : images.project1
                            }
                          ></img>
                          <div className="select-project-text">
                            {item.name}
                            <span>{item?.organization?.name}</span>
                          </div>
                        </div>
                        {item.ref === projectRef && (
                          <img
                            className="select-check-icon"
                            src={images.CheckGreenIcon}
                            alt=""
                          />
                        )}
                      </div>
                    </MenuItem>
                  ))}
                </MaterialSelect>
              </div>
            </form>

            <ul className="navbar-nav ml-auto">
              {/* Alert */}
              <li className={showNotification ? "nav-item show" : "nav-item"}>
                <a
                  className="nav-link"
                  target="_blank"
                  rel="noreferrer"
                  href="https://voyagecontrol.com/support-delivery"
                  role="button"
                >
                  <img src={images.alert} alt="alert" />
                </a>
              </li>
              {/* Notification */}

              <li
                className="nav-item dropdown no-arrow mr-3"
                ref={refOutsideNotification}
              >
                <Link
                  className="nav-link dropdown-toggle"
                  to=""
                  id="alertsDropdown"
                  role="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClickNotification();
                  }}
                >
                  <img src={images.notification} alt="notification" />
                  {unreadCount > 0 && (
                    <span className="badge-counter">{unreadCount}</span>
                  )}
                </Link>
              </li>
              <AccountMenu
                initials={initials}
                options={[
                  {
                    Icon: icons.Profile,
                    label: t("Translation.my_account"),
                    onClick: () => {
                      navigate(routes.profile.account);
                    },
                  },
                  {
                    Icon: icons.PeopleThree,
                    label: t("Translation.my_organization"),
                    onClick: () => {
                      navigate(routes.profile.organization);
                    },
                  },
                  {
                    Icon: icons.AlarmBell,
                    label: t("Translation.notification_preferences"),
                    onClick: () => {
                      navigate(routes.profile.notification);
                    },
                  },
                  {
                    Icon: icons.Component,
                    label: t("Translation.select_platform"),
                    onClick: () => {
                      window.location.href = CHOOSE_PLATFORM_PAGE;
                    },
                  },
                  {
                    Icon: icons.LogoutCircle,
                    label: t("Translation.logout"),
                    onClick: logout,
                  },
                ]}
              />
            </ul>
          </nav>
        </div>
      </div>

      <div className="mobile_custom_navbar" id="navbar-mobile">
        <nav className="navbar navbar-dark">
          <Link
            className="sidebar-brand d-flex mb-0"
            to={routes.dashboardNav.home(projectRef)}
          >
            <img src={images.MobileLogo} alt="mobile-logo" />
          </Link>
          <button
            className="navbar-toggler"
            onClick={() => setShowMenu(!showMenu)}
          >
            <img src={images.HamburgerMenuIcon} alt="menuicon" />
          </button>
        </nav>

        <div
          className={`collapse ${showMenu ? "show" : ""}`}
          id="navbarToggleExternalContent"
        >
          <div>
            <div className="custom-menu-mobile-links">
              <ul>
                {headerMenu.map((item: any) => (
                  <li key={item.name}>
                    <Link
                      className="dropdown-item"
                      to={`${item.redirectLink}`}
                      key={item.name}
                    >
                      <span>{item.name}</span>
                    </Link>
                  </li>
                ))}
                <li className={showNotification ? "nav-item show" : "nav-item"}>
                  <a
                    className="nav-link"
                    target="_blank"
                    rel="noreferrer"
                    href="https://voyagecontrol.com/support-delivery"
                    role="button"
                  >
                    {t("Translation.help")}
                  </a>
                </li>
                <li>
                  <Link
                    onClick={logout}
                    className="dropdown-item"
                    to={routes.auth.login}
                    key="logout"
                  >
                    <span>{t("Translation.logout")}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default index;
